import { useTranslation } from "react-i18next";
import { Accordion } from "../ui/Accordion";
import { sanitizeMenuHtml } from "../shared/sanitizeMenuHtml";
import React, { useState, useEffect, useCallback, useId, useMemo, useRef } from "react";
import type { CustomerPreOrderItemUpdateRequest } from "../../../common/jsonRpcTypes";
import ConfigurableOptionsModal from "./ConfigurableOptionsModal";
import MenuInfoAlert from "./MenuInfoAlert";
import { isAbortedRequest, useRpcCaller } from "../context/WidgetRequestContext";
import QuantityControls from "./QuantityControls";
import MenuItemCard from "../shared/MenuItemCard";
import AllergenWarning from "../shared/AllergenWarning";
import {
  convertOptionsToSelectedOptions,
  getActualItemCountForMenu,
  getItemCalories,
  getMenuMultiplier,
  getPersonItemQuantity,
  getPersonItemRows,
  isItemSafeForPersonAllergies,
  resolveTypedQuantity,
  type MenuLimits,
} from "./preOrderUtils";
import { useItemHighlight } from "../hooks/useItemHighlight";
import {
  validateItemAddition as validateItemAdditionUtil,
  calculateLimitValue,
  validateAllMenus as validateAllMenusUtil,
  getMinimumSpendErrors,
  type MinimumSpendError,
} from "../../utils/menuValidation";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { usePreorderContext, type LinkedBooking } from "../context/PreorderContext";

interface MenuAmountTarget {
  id: string;
  selected_configurable_options?: CustomerPreOrderItemUpdateRequest["selectedOptions"];
}

interface MenuProps {
  onValidationChange?: (isValid: boolean) => void;
  onMenuLimitsChange?: (limits: MenuLimits) => void;
  externalValidationErrors?: { [menuId: string]: string };
  isLinkedBooking?: boolean;
  linkedBookings?: LinkedBooking[] | null;
}

const Menu: React.FC<MenuProps> = ({
  onValidationChange,
  onMenuLimitsChange,
  externalValidationErrors = {},
  isLinkedBooking = false,
  linkedBookings = null,
}) => {
  const call = useRpcCaller();
  const {
    customerPreOrder,
    updateCustomerPreOrder,
    isItemPending,
    forNames,
    orderingFor,
    multipleNames,
    hidePricesByMenu,
    bookingTypeId,
  } = usePreorderContext();
  const { t } = useTranslation();
  const allergenWarningIdPrefix = useId();

  // Use linked bookings menus when isLinkedBooking is true
  // Memoize to prevent infinite loops in useEffect
  const menus = useMemo(() => {
    if (isLinkedBooking && linkedBookings) {
      // Flatten all menus from all linked bookings
      const linkedMenus = linkedBookings.flatMap((linkedBooking) => linkedBooking.menus);
      return linkedMenus;
    }
    return customerPreOrder?.menus ?? [];
  }, [isLinkedBooking, linkedBookings, customerPreOrder?.menus]);

  const bookingId = customerPreOrder?.booking?._id;
  const currency = customerPreOrder?.booking?.venue?.currency ?? "GBP";

  // Helper function to get booking_type from menuId
  const getBookingTypeFromMenuId = useCallback(
    (menuId: string): string | undefined => {
      // For linked bookings, find the booking type from the linked booking structure
      if (isLinkedBooking && linkedBookings) {
        for (const linkedBooking of linkedBookings) {
          const menuInLinkedBooking = linkedBooking.menus.find((m) => m.id === menuId);
          if (menuInLinkedBooking) {
            return linkedBooking.bookingTypeId;
          }
        }
      }

      // First, try to get the booking type from the menu's own booking_type property
      const menu = menus.find((m) => m.id === menuId);
      if (menu?.booking_type) {
        return menu.booking_type;
      }

      // Fallback to the bookingTypeId from the createBooking response
      // This is the authoritative source for the booking type in the /book flow
      if (bookingTypeId) {
        return bookingTypeId;
      }

      // Final fallback
      return undefined;
    },
    [isLinkedBooking, linkedBookings, menus, bookingTypeId],
  );
  const countryCode = customerPreOrder?.booking?.venue?.country_code ?? "GB";

  // State for configurable options modal
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const optionsSessionRef = useRef(0);
  const [currentOptions, setCurrentOptions] = useState<any[]>([]);
  const [currentItem, setCurrentItem] = useState<{ id: string; name: string; configurable_options?: any[] } | null>(
    null,
  );
  const [currentMenuId, setCurrentMenuId] = useState<string>("");
  const [currentChangeAmount, setCurrentChangeAmount] = useState<number>(0);
  const [processedMenusData, setProcessedMenusData] = useState<{ [menuId: string]: any }>({});
  const [openStates, setOpenStates] = useState<boolean[]>(menus.map((_: any, idx: number) => idx === 0));

  // Track selected values for each option label
  const [selectedOptions, setSelectedOptions] = useState<{ [optionLabel: string]: { [valueId: string]: boolean } }>({});
  const [validationErrors, setValidationErrors] = useState<{ [menuId: string]: string }>({});

  const { highlightedItems, highlight: highlightItem } = useItemHighlight();

  // Helper to get min/max text (dummy for now, can be extended)
  const getOptionMinMaxText = (option: any) => {
    if (option.min || option.max) {
      const maxText = option.max ? `, up to ${option.max}` : "";
      return `Choose at least ${option.min || 0}${maxText} option(s) `;
    }
    return null;
  };

  useEffect(() => {
    // Skip fetching processed menu data for linked bookings since they use different booking context
    if (!bookingId || isLinkedBooking) return;
    const fetchMenuData = async () => {
      const menuDataPromises = menus.map(async (menu: any) => {
        try {
          const data = await call("/getProcessedMenuData", {
            booking_id: bookingId,
            menuId: menu.id,
            ...(customerPreOrder?.pending ? { pending: true } : {}),
          });
          return { menuId: menu.id, data };
        } catch (error) {
          return { menuId: menu.id, data: null };
        }
      });

      const results = await Promise.all(menuDataPromises);
      const menuDataMap: { [menuId: string]: any } = {};
      for (const { menuId, data } of results) {
        if (data) {
          menuDataMap[menuId] = data;
        }
      }
      setProcessedMenusData(menuDataMap);
    };

    if (menus.length > 0) {
      fetchMenuData();
    }
    // Use menus as dependency (memoized to prevent unnecessary refetches)
  }, [call, menus, bookingId, isLinkedBooking, customerPreOrder?.pending]);

  // Use a stable key for each option (prefer id, then name, then label, then idx)
  const getOptionKey = (option: any, idx: number) => option.id || option.name || option.label || idx;
  const getValueKey = (value: any, vIdx: number) => value.id || value.name || value.label || vIdx;

  const handleOptionValueChange = (optionKey: string, valueKey: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionKey]: {
        ...prev[optionKey],
        [valueKey]: !prev[optionKey]?.[valueKey],
      },
    }));
  };

  const showOptionsValidationError = (errors: string[], session: number): void => {
    if (optionsSessionRef.current !== session) return;
    const errorMessage = `${t("configurable_options_error", "Please fix the following issues:")}\n${errors.join("\n")}`;
    alert(errorMessage);
  };

  const reportOptionsConfirmError = <T,>(error: T): void => {
    if (isAbortedRequest(error)) return;
    console.error("Failed to confirm configurable options:", error);
    alert(t("configurable_options_confirm_error", "Failed to update item. Please try again."));
  };

  // Validates and saves the confirmed options; resolves true only once the item is in the basket.
  const submitConfiguredItem = async (
    session: number,
    item: NonNullable<typeof currentItem>,
    menuId: string,
    preOrder: NonNullable<typeof customerPreOrder>,
  ): Promise<boolean> => {
    const validationResult = await call("/validateConfigurableOptions", {
      booking_id: preOrder.booking._id,
      itemId: item.id,
      menuId,
      selectedOptions,
      ...(preOrder.pending ? { pending: true } : {}),
    });

    if (!validationResult.valid) {
      showOptionsValidationError(validationResult.errors, session);
      return false;
    }

    const itemId = typeof item === "string" ? item : item.id;
    const updated = await updateCustomerPreOrder({
      booking_id: preOrder.booking._id,
      pendingPreorderId: preOrder.pendingPreorderId ?? null,
      itemId,
      menuId,
      amount: currentChangeAmount,
      selectedOptions,
      multipleNames,
      forNames,
      orderingFor,
      booking_type: getBookingTypeFromMenuId(menuId),
    });

    // A failed, timed-out or aborted update resolves false; keep the modal open so the guest can retry.
    if (!updated) return false;
    if (optionsSessionRef.current === session) setShowOptionsModal(false);
    highlightItem(`${itemId}_${menuId}_${orderingFor}`);
    return true;
  };

  // Confirm options selection
  const handleOptionsConfirm = async () => {
    const session = optionsSessionRef.current;

    if (currentItem && currentMenuId && customerPreOrder) {
      try {
        const added = await submitConfiguredItem(session, currentItem, currentMenuId, customerPreOrder);
        if (!added) return;
      } catch (error) {
        // Keep the selection so the guest can confirm again.
        reportOptionsConfirmError(error);
        return;
      }
    }
    if (optionsSessionRef.current === session) {
      setCurrentOptions([]);
      setCurrentItem(null);
      setCurrentMenuId("");
      setCurrentChangeAmount(0);
    }
  };

  const handleAccordionHeaderClick = (idx: number) => {
    setOpenStates((prev) => {
      const newStates = [...prev];
      newStates[idx] = !newStates[idx];
      return newStates;
    });
  };

  const activeKeys = openStates
    .map((isOpen, idx) => (isOpen ? String(idx) : undefined))
    .filter((v): v is string => v !== undefined);

  const handleAmountChangeAbs = async (
    menuId: string,
    item: MenuAmountTarget,
    newQuantity: number,
    selectedOptions: CustomerPreOrderItemUpdateRequest["selectedOptions"] = item.selected_configurable_options,
  ): Promise<boolean> => {
    if (!customerPreOrder) return false;
    const itemId = item.id;
    // The in-flight update already locked this row — a second tap must not send another request.
    if (isItemPending(itemId, menuId, orderingFor)) return false;
    const itemKey = `${itemId}_${menuId}_${orderingFor}`;

    try {
      const updated = await updateCustomerPreOrder({
        booking_id: customerPreOrder.booking._id,
        pendingPreorderId: customerPreOrder.pendingPreorderId ?? null,
        itemId,
        menuId,
        amount: newQuantity,
        selectedOptions,
        multipleNames,
        forNames,
        orderingFor,
        booking_type: getBookingTypeFromMenuId(menuId),
      });
      // Highlight the item in the menu
      if (updated) highlightItem(itemKey);
      return updated;
    } catch (error) {
      console.error("Failed to update item amount:", error);
      alert(t("update_item_error", "Failed to add item. Please try again."));
      return false;
    }
  };

  const decreaseMenuQuantity = async (
    menuId: string,
    itemId: string,
    quantity: number,
    person: number | undefined,
  ): Promise<void> => {
    if (!customerPreOrder || quantity <= 0) return;

    const rows = getPersonItemRows(customerPreOrder.items ?? [], itemId, menuId, person);
    let remaining = quantity;

    for (let index = rows.length - 1; index >= 0 && remaining > 0; index--) {
      const row = rows[index];
      if (!row) continue;

      const amountToRemove = Math.min(row.quantity, remaining);
      const selectedOptions = row.options?.length ? convertOptionsToSelectedOptions(row.options) : undefined;
      const updated = await handleAmountChangeAbs(menuId, { id: itemId }, -amountToRemove, selectedOptions);
      if (!updated) return;
      remaining -= amountToRemove;
    }
  };

  const handleConfigurableOptionsSetup = (item: any, menuId: string, changeAmount: number) => {
    optionsSessionRef.current += 1;
    setCurrentOptions(item.configurable_options);
    setCurrentItem(item);
    setCurrentMenuId(menuId);
    setCurrentChangeAmount(changeAmount);

    const initialSelected: { [optionKey: string]: { [valueKey: string]: boolean } } = {};
    for (let idx = 0; idx < item.configurable_options.length; idx++) {
      const option = item.configurable_options[idx];
      const optionKey = getOptionKey(option, idx);
      initialSelected[optionKey] = {};
      if (option.values) {
        for (let vIdx = 0; vIdx < option.values.length; vIdx++) {
          const value = option.values[vIdx];
          const valueKey = getValueKey(value, vIdx);
          initialSelected[optionKey][valueKey] = !!value.selected;
        }
      }
    }
    setSelectedOptions(initialSelected);
    setShowOptionsModal(true);
  };

  const validateItemAddition = (
    menuId: string,
    itemId: string,
    quantity: number,
  ): { valid: boolean; message?: string } => {
    const menu = customerPreOrder?.menus.find((entry) => entry.id === menuId);
    const item = menu?.items.filter((entry) => entry.type !== "heading").find((entry) => entry.id === itemId);
    return validateItemAdditionUtil(
      menuId,
      quantity,
      processedMenusData,
      customerPreOrder?.items ?? [],
      customerPreOrder?.booking?.num_people,
      multipleNames && forNames.length > 0 ? orderingFor : undefined,
      menu && item ? { menu, item, quantity } : undefined,
    );
  };

  // Function to validate all menus and check if ordering is allowed
  const validateAllMenus = useCallback((): boolean => {
    if (!customerPreOrder) return false;
    return validateAllMenusUtil(
      customerPreOrder.items ?? [],
      customerPreOrder.booking?.num_people,
      menus,
      processedMenusData,
    );
  }, [customerPreOrder, menus, processedMenusData]);

  // Effect to notify parent component when menu limits change
  useEffect(() => {
    if (!customerPreOrder || !onMenuLimitsChange || Object.keys(processedMenusData).length === 0) return;

    const guestCount = customerPreOrder.booking?.num_people;
    const limits: MenuLimits = {};

    for (const menuId of Object.keys(processedMenusData)) {
      const menuData = processedMenusData[menuId];
      const limitPreorders = menuData?.menuSettings?.limit_preorders;
      if (limitPreorders) {
        limits[menuId] = {
          max: calculateLimitValue(limitPreorders.max, guestCount),
          min: calculateLimitValue(limitPreorders.min, guestCount),
        };
      }
    }

    onMenuLimitsChange(limits);
  }, [processedMenusData, onMenuLimitsChange, customerPreOrder]);

  // Track minimum spend errors per menu
  const minimumSpendErrors = useMemo((): MinimumSpendError[] => {
    if (!customerPreOrder) return [];
    return getMinimumSpendErrors(menus, customerPreOrder.items ?? [], customerPreOrder.booking?.num_people);
  }, [customerPreOrder, menus]);

  // Effect to notify parent component when validation status changes
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validateAllMenus();
      onValidationChange(isValid);
    }
  }, [validateAllMenus, onValidationChange]);

  // Effect to clear validation errors when items are removed and count goes back under the limit
  useEffect(() => {
    if (!customerPreOrder) return;
    const currentItems = customerPreOrder.items ?? [];
    const guestCount = customerPreOrder.booking?.num_people;

    setValidationErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      let hasChanges = false;

      for (const menuId of Object.keys(newErrors)) {
        if (!newErrors[menuId]) continue;

        const menuData = processedMenusData[menuId];
        const maxLimit = menuData?.menuSettings?.limit_preorders?.max;
        if (!maxLimit) continue;

        // Use getActualItemCountForMenu to properly count nested items in fixed price menus
        const currentCount = getActualItemCountForMenu(currentItems, menuId);

        const maxItems = calculateLimitValue(maxLimit, guestCount);

        if (!maxItems || currentCount < maxItems) {
          newErrors[menuId] = "";
          hasChanges = true;
        }
      }

      return hasChanges ? newErrors : prevErrors;
    });
  }, [customerPreOrder, processedMenusData]);

  const addItemWithOptions = async (menuId: string, item: any, quantity: number = 1) => {
    setValidationErrors((prev) => ({ ...prev, [menuId]: "" }));
    quantity = quantity * (processedMenusData[menuId]?.menuSettings?.item_multiplier || 1);
    const validation = validateItemAddition(menuId, item.id, quantity);
    if (!validation.valid) {
      setValidationErrors((prev) => ({ ...prev, [menuId]: validation.message ?? "Invalid item addition" }));
      return;
    }

    if (!item.configurable_options?.length) {
      return handleAmountChangeAbs(menuId, item, quantity);
    }

    handleConfigurableOptionsSetup(item, menuId, quantity);
  };

  const handleMenuQuantityCommit = async (
    menuId: string,
    item: MenuAmountTarget,
    currentQuantity: number,
    person: number | undefined,
    value: number | null,
  ): Promise<void> => {
    const multiplier = getMenuMultiplier(customerPreOrder ?? undefined, menuId);
    const desiredQuantity = resolveTypedQuantity(value, multiplier);
    const delta = desiredQuantity - currentQuantity;

    if (delta > 0) {
      await addItemWithOptions(menuId, item, delta / multiplier);
    } else if (delta < 0) {
      await decreaseMenuQuantity(menuId, item.id, -delta, person);
    }
  };

  const orderingPerson = multipleNames && forNames.length > 0 ? orderingFor : undefined;
  const dietaryPersonIndex = orderingPerson ?? 0;
  const personDietaryRequirements = customerPreOrder?.dietaryRequirements?.[dietaryPersonIndex];
  const dietaryPersonName = orderingPerson === undefined ? undefined : forNames[orderingPerson];

  // Utility to strip HTML tags
  function stripHtmlTags(html: string): string {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  }

  if (!customerPreOrder) {
    return null;
  }

  return (
    <>
      <ConfigurableOptionsModal
        show={showOptionsModal}
        options={currentOptions}
        selectedOptions={selectedOptions}
        onClose={() => setShowOptionsModal(false)}
        onConfirm={handleOptionsConfirm}
        getOptionKey={getOptionKey}
        getValueKey={getValueKey}
        getOptionMinMaxText={getOptionMinMaxText}
        handleOptionValueChange={handleOptionValueChange}
        itemName={currentItem?.name ?? ""}
        currency={currency}
        countryCode={countryCode}
        hidePrices={hidePricesByMenu[currentMenuId] ?? false}
      />
      {menus.length === 0 ? (
        <div className="gcw:text-gray-500 gcw:italic gcw:p-4">
          {isLinkedBooking ? "No menus available for this linked booking" : "No menus available"}
        </div>
      ) : (
        <Accordion activeKey={activeKeys} alwaysOpen>
          {menus.map((menu: any, menuIdx: number) => {
            const menuData = processedMenusData[menu.id];
            // Use processed menu data if available, otherwise fall back to raw menu data
            const menuSettings =
              menuData?.menuSettings || (menu.limit_preorders ? { limit_preorders: menu.limit_preorders } : {});
            const fixedPriceInfo =
              menuData?.fixedPriceInfo ||
              (menu.fixed_price_rules?.length ? { isFixedPrice: true } : { isFixedPrice: false });
            const menuInfo = menuData?.menuInfo || {
              name: menu.name,
              description: menu.description || "",
              displayText: {},
            };

            // Only use per-menu hidePricesByMenu
            const menuHidePrices = hidePricesByMenu[menu.id] ?? false;

            // Don't render if menuInfo.name is null or empty
            if (!menuInfo.name || menuInfo.name.trim() === "") {
              return null;
            }

            return (
              <Accordion.Item
                eventKey={String(menuIdx)}
                key={menu.id}
                className={`${openStates[menuIdx] ? "menu-open" : "menu-closed"}${fixedPriceInfo.isFixedPrice ? " fixed-price-menu" : ""}`}
              >
                {fixedPriceInfo.isFixedPrice && (
                  <span className="fixed-price-menu-label">{t("fixed_price_menu", "Fixed price menu")}</span>
                )}
                <Accordion.Header onClick={() => handleAccordionHeaderClick(menuIdx)}>
                  <div className="gcw:flex gcw:flex-col pre-order-info">
                    <span className="pre-order-section-title">{menuInfo.name}</span>
                    {stripHtmlTags(menuInfo.description).length > 0 && (
                      <div
                        className="menu-info-description gcw:mt-2"
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: Sanitised via DOMPurify
                        dangerouslySetInnerHTML={{ __html: sanitizeMenuHtml(menuInfo.description) }}
                      />
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body className="gc-accordion-body-menu gcw:mb-4">
                  <MenuInfoAlert menuInfo={menuInfo} fixedPriceInfo={fixedPriceInfo} hidePrices={menuHidePrices} />
                  {(() => {
                    const spendError = minimumSpendErrors.find((e) => e.menuId === menu.id);
                    if (!spendError) return null;

                    return (
                      <div
                        className="gcw:flex gcw:flex-col gcw:items-start gcw:m-4 gcw:p-4 gcw:rounded-md gcw:border gcw:bg-alert-warning gcw:border-alert-warning-border gcw:text-alert-text-warning"
                        role="alert"
                      >
                        <strong>{t("minimum_spend_requirement", "Minimum spend requirement")}</strong>
                        <div>
                          {t("booking_preorder_minimum_spend", "Minimum spend of {{amount}} required for {{menu}}.", {
                            amount: formatCurrency(spendError.requiredAmount, currency, countryCode),
                            menu: menuInfo.name,
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    if (!menuSettings.limit_preorders) return null;

                    const currentItems = customerPreOrder?.items ?? [];
                    // Use getActualItemCountForMenu to properly count nested items in fixed price menus
                    const currentCount = getActualItemCountForMenu(currentItems, menu.id);

                    const minItems = menuSettings.limit_preorders.min || 0;
                    const maxItems = menuSettings.limit_preorders.max || 0;
                    const neededItems = Math.max(0, minItems - currentCount);

                    // Show warning if there are limits defined and user needs to add/remove items
                    const showWarning =
                      (minItems > 0 && currentCount < minItems) || (maxItems > 0 && currentCount > maxItems);

                    if (!showWarning) return null;

                    return (
                      <div
                        className="gcw:flex gcw:flex-col gcw:items-start gcw:m-4 gcw:p-4 gcw:rounded-md gcw:border gcw:bg-alert-warning gcw:border-alert-warning-border gcw:text-alert-text-warning"
                        role="alert"
                      >
                        <strong>{t("Pre-ordering limited", "Order numbers restrictions")}</strong>
                        {maxItems > 0 ? (
                          <div>
                            {t("preorder_limited_message_min_max", {
                              currentItems: currentCount,
                              menuName: menuInfo.name,
                              minItems,
                              maxItems,
                              neededItems,
                            })}
                          </div>
                        ) : (
                          <div>
                            {t("preorder_limited_message_min", {
                              currentItems: currentCount,
                              menuName: menuInfo.name,
                              minItems,
                              maxItems,
                              neededItems,
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(validationErrors[menu.id] || externalValidationErrors[menu.id]) && (
                    <div
                      className="gcw:flex gcw:flex-col gcw:items-start gcw:m-4 gcw:p-4 gcw:rounded-md gcw:border gcw:border-error gcw:bg-red-50 gcw:text-red-800 widget-error-panel gcw:text-body"
                      role="alert"
                    >
                      <strong>{t("Pre-ordering limited", "Order numbers restrictions")}</strong>
                      <div>{validationErrors[menu.id] || externalValidationErrors[menu.id]}</div>
                    </div>
                  )}
                  <ul className="menu-list">
                    {menu.items.map((item: any) => {
                      if (item.type === "heading") {
                        return (
                          <li
                            key={item.value}
                            className="gcw:mx-4 gcw:font-base gcw:bg-transparent gcw:!border-0 menu-heading-text"
                          >
                            <strong>{item.value}</strong>
                          </li>
                        );
                      }

                      const processedItem = menuData?.processedItems?.find((pi: any) => pi.id === item.id) || item;
                      const containsAllergen = !isItemSafeForPersonAllergies(
                        item,
                        dietaryPersonIndex,
                        customerPreOrder?.dietaryRequirements,
                      );

                      // Use raw item data as fallback for linked bookings
                      const itemPrice = Number(processedItem.price) || Number(item.price) || 0;
                      const itemAllergenInfo = processedItem.allergenInfo || item.allergen_info;
                      // Linked-booking menus never fetch the processed menu data,
                      // so fall back to the raw item the booking response carried.
                      const itemCalories = getItemCalories(processedItem) ?? getItemCalories(item);
                      const itemProcessedDiscounts = processedItem.processedDiscounts || null;

                      const quantity = getPersonItemQuantity(
                        customerPreOrder.items ?? [],
                        item.id,
                        menu.id,
                        orderingPerson,
                      );
                      const isInBasket = quantity > 0;

                      // Check if this item is highlighted (recently added)
                      const menuItemKey = `${item.id}_${menu.id}_${orderingFor}`;
                      const isHighlighted = highlightedItems.has(menuItemKey);

                      const allergenWarningId = containsAllergen
                        ? `${allergenWarningIdPrefix}-${menu.id}-${item.id}`
                        : undefined;

                      return (
                        <li
                          key={item.id}
                          className={`gcw:mx-4 ${isInBasket ? "selected-item " : ""}${containsAllergen ? "menu-item-unsuitable" : ""}${isHighlighted ? " basket-item-highlight" : ""}`}
                        >
                          <MenuItemCard
                            variant="menu"
                            name={item.name}
                            description={item.description}
                            calories={itemCalories}
                            price={itemPrice}
                            allergenInfo={itemAllergenInfo}
                            dietTypes={item.diet_types}
                            personDietTypes={personDietaryRequirements?.diet_types ?? []}
                            warning={
                              containsAllergen && (
                                <AllergenWarning id={allergenWarningId} personName={dietaryPersonName} />
                              )
                            }
                            hidePrices={menuHidePrices}
                            isFixedPrice={fixedPriceInfo.isFixedPrice}
                            processedDiscounts={itemProcessedDiscounts}
                            currency={currency}
                            countryCode={countryCode}
                            discountKeyPrefix={`${menu.id}-${item.id}`}
                            actions={
                              <QuantityControls
                                variant="menu"
                                quantity={quantity}
                                itemName={item.name}
                                isLoading={isItemPending(item.id, menu.id, orderingFor)}
                                disabled={containsAllergen}
                                describedBy={allergenWarningId}
                                onIncrease={() => addItemWithOptions(menu.id, item, 1)}
                                onDecrease={() =>
                                  decreaseMenuQuantity(
                                    menu.id,
                                    item.id,
                                    getMenuMultiplier(customerPreOrder, menu.id),
                                    orderingPerson,
                                  )
                                }
                                onQuantityCommit={(value) =>
                                  handleMenuQuantityCommit(menu.id, item, quantity, orderingPerson, value)
                                }
                              />
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </>
  );
};

export { Menu };
